import { countFormulaSymbols } from '@/shared/features/rich-text/utils/countFormulaSymbols';

/** 글자 수 안내를 시작하는 비율. 플레이 주관식 답(50자 중 45자부터)과 같다. */
const NEAR_LIMIT_RATIO = 0.9;

export type EquationCharacterBudget = {
  /** 수식이 들어가는 칸의 글자 한도 */
  limit: number;
  /** 지금 편집하는 수식을 뺀 칸의 나머지 글자 수 */
  otherCount: number;
  /** 수식 편집 창을 열 때의 칸 총합. 이미 한도를 넘은 옛 값은 이보다 늘지 않으면 허용한다. */
  initialTotal: number;
};

export type EquationHelper = 'nearLimit' | 'overLimit' | 'latexTooLong' | null;

const getHelper = ({
  total,
  limit,
  isLatexTooLong,
}: {
  total: number | null;
  limit?: number;
  isLatexTooLong: boolean;
}): EquationHelper => {
  // 원문 초과는 서버가 거절하는 사유라 글자 수를 줄여도 풀리지 않는다 — 먼저 알린다.
  if (isLatexTooLong) return 'latexTooLong';
  if (total !== null && limit !== undefined && total > limit)
    return 'overLimit';
  if (
    total !== null &&
    limit !== undefined &&
    total >= Math.ceil(limit * NEAR_LIMIT_RATIO)
  )
    return 'nearLimit';
  return null;
};

/**
 * 수식 편집 창의 안내 문구와 입력 버튼 상태. `budget` 이 있으면 수식이 들어가는 칸의
 * 전체 글자 수 = 나머지 글자 + 이 수식의 보이는 글자로 판단한다. 수식 하나의 LaTeX 원문
 * 상한(`maxLatexLength`)은 서버 규칙이라 글자 수가 남아도 따로 막는다.
 */
export const getEquationBudgetState = ({
  latex,
  maxLatexLength,
  budget,
}: {
  latex: string;
  maxLatexLength?: number;
  budget?: EquationCharacterBudget;
}): { total: number | null; helper: EquationHelper; canSubmit: boolean } => {
  const isLatexTooLong =
    maxLatexLength !== undefined && latex.length > maxLatexLength;
  const total = budget ? budget.otherCount + countFormulaSymbols(latex) : null;
  const isGrowingOverLimit =
    !!budget &&
    total !== null &&
    total > budget.limit &&
    total > budget.initialTotal;

  return {
    total,
    helper: getHelper({ total, limit: budget?.limit, isLatexTooLong }),
    canSubmit:
      !!latex.trim() &&
      !latex.includes('placeholder') &&
      !isLatexTooLong &&
      !isGrowingOverLimit,
  };
};
